import { Formik, Form } from 'formik';
import { transparentize } from 'polished';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { object, string } from 'yup';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { Box } from '@/components/uielements/box/box';
import { Input } from '@/components/uielements/formik';
import { Modal } from '@/components/uielements/modal';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import {
  StyledRewardsButtonHeader,
  StyledRewardsFooterBox,
} from '@/components/uielements/reward/styles';
import { CSS_CLASSES } from '@/constants/cssClassNames';
import { useStrings } from '@/redux/cmsConfig';
import { getFontStyles } from '@/redux/cmsConfig/utils';
import meActions from '@/redux/me/actions';
import { meSliceSelector } from '@/redux/me/reducer';
import { safePolished } from '@/utils/safePolished';
import { safelyGetString } from '@/utils/stringHelpers';

// Validation schema for the barcode submission form.
const barcodeSchema = object({
  barcode: string(),
});

export const RedeemReceipt = () => {
  const dispatch = useDispatch();
  const { receiptBarcodeLoading } = useSelector(meSliceSelector);
  const strings = useStrings();

  return (
    <StyledRewardsFooterBox className={CSS_CLASSES.LOYALTY_REWARDS_FOOTER.RECEIPT_BARCODE}>
      <StringAccessor tag={StyledRewardsButtonHeader} accessor="rewards.enter_barcode_header" />

      <Modal.Container
        trigger={(open) => (
          <StyledPrimaryButton
            data-css-override="StyledPrimaryButton"
            onClick={open}
            $buttonTheme="knockout"
            aria-label="Click to redeem rewards from a receipt barcode"
          >
            <StringAccessor accessor="rewards.enter_barcode_button" />
          </StyledPrimaryButton>
        )}
      >
        {(toggle) => (
          <>
            <Modal.Title>
              <StringAccessor accessor="rewards.enter_barcode_modal_header" html={true} />
            </Modal.Title>

            <Formik
              initialValues={{ barcode: '' }}
              onSubmit={({ barcode }, { resetForm }) => {
                dispatch(meActions.redeemReceiptBarcode(barcode));
                toggle();
                resetForm();
              }}
              validationSchema={barcodeSchema}
            >
              {({ dirty, isValid }) => (
                <Form>
                  <Modal.Content>
                    <Description>
                      <StringAccessor
                        accessor="rewards.enter_barcode_instructional_text"
                        html={true}
                      />
                    </Description>

                    <Box
                      overrides={{
                        marginTop: 'var(--size-3)',
                      }}
                    >
                      <Input
                        autoFocus
                        id="barcode"
                        name="barcode"
                        label={safelyGetString(strings, 'rewards.enter_barcode_placeholder_text')}
                      />
                    </Box>

                    <Disclaimer>
                      <StringAccessor
                        accessor="rewards.enter_barcode_disclaimer_text"
                        html={true}
                      />
                    </Disclaimer>
                  </Modal.Content>

                  <Modal.Footer>
                    <Modal.Cancel>Close</Modal.Cancel>

                    <StyledPrimaryButton
                      data-css-override="StyledPrimaryButton"
                      disabled={!dirty || !isValid}
                      $submitting={receiptBarcodeLoading}
                      $buttonTheme="secondary"
                      type="submit"
                    >
                      Submit
                    </StyledPrimaryButton>
                  </Modal.Footer>
                </Form>
              )}
            </Formik>
          </>
        )}
      </Modal.Container>
    </StyledRewardsFooterBox>
  );
};

const Description = styled.p`
  font-size: 1rem;
  margin: 0;
`;

const Disclaimer = styled.p(({ theme }) => ({
  fontSize: '.75rem',
  margin: 0,
  ...getFontStyles(theme.text.primary_font, ['color'], {
    color: (value) => (value ? safePolished(transparentize)('0.1', value) : 'inherit'),
  }),
}));
